import { BaseService } from "./BaseService";

export interface LoginRequest {
  username: string;
  password: string;
  tenantId: string;
}

export interface User {
  id: string;
  userId?: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  region: string;
  tenantId: string;
  role?: string;
  hotelId?: string;
  hotelIds?: string[];
  realmRoles: string[];
  clientRoles: {
    [key: string]: {
      roles: string[];
    };
  };
  permissions: AuthPermission[];
  sessionState: string;
  enabled: boolean;
  emailVerified: boolean;
  attributes: Record<string, unknown>;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
  user: User;
}

export interface LoginAsUserRequest {
  userId: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
  tenantId: string;
}

export type RefreshTokenResponse = LoginResponse;

export interface LogoutResponse {
  url: string;
}

export interface LoginUrlRequest {
  email: string;
  redirectUri: string;
}

export interface LoginUrlResponse {
  url: string;
  state?: string;
}

export interface ExchangeCodeRequest {
  code: string;
  state?: string | null;
  redirectUri: string;
  iss: string;
}

export interface CatalogUser {
  id: string;
  status?: string;
  openIdUserId: string;
  lastLogin?: Date;
  createdAt: Date;
  updatedAt: Date;
  tenantId: string;
  role: string;
  permissions?: AuthPermission[];
  platformPermissions?: string[];
  tenantPermissions?: string[];
  hotelIds?: string[];
  tenantIds?: string[];
  effectiveRole?: EffectiveRole | null;
}

export interface AuthPermission {
  name: string;
  description?: string;
}

export interface EffectiveRoleCustomDetails {
  id: string;
  name: string;
}

export interface EffectiveRole {
  type: "BASE" | "CUSTOM";
  baseRole: string;
  customRole?: EffectiveRoleCustomDetails | null;
}

export interface RegionUserProfile {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  language?: string;
  preferences?: unknown;
  hotelId?: string;
  tenantId: string;
  dateOfBirth?: Date;
  photoId?: string;
  preferredLanguage: string;
}

export interface MeAccessFeature {
  key: string;
  type: "BOOLEAN" | "NUMBER";
  value: boolean | number | "unlimited";
}

export interface MeCurrentPlan {
  id: string;
  title: string;
  enableCoreFeatures: boolean;
}

export interface UserAccess {
  hasActiveSubscription: boolean;
  enableCoreFeatures?: boolean;
  currentPlan: MeCurrentPlan | null;
  features: MeAccessFeature[] | null;
  tenantSubscriptionId: string | null;
  paymentUrl: string | null;
}

export interface UserProfile extends CatalogUser {
  profile?: Omit<
    RegionUserProfile,
    | "emailIv"
    | "phoneIv"
    | "phoneAuthTag"
    | "emailAuthTag"
    | "createdAt"
    | "updatedAt"
  > | null;
  access?: UserAccess | null;
}

class AuthService extends BaseService {
  private basePath = "/auth";

  constructor() {
    super();
  }

  async login(loginData: LoginRequest): Promise<LoginResponse> {
    return this.post<LoginResponse>(`${this.basePath}/login`, loginData);
  }

  async loginAsUser(data: LoginAsUserRequest): Promise<LoginResponse> {
    return this.request<LoginResponse>(`${this.basePath}/login-as-user`, {
      method: "POST",
      body: JSON.stringify(data),
      disableToast: true,
    });
  }

  async tenantAdminLoginAsUser(
    data: LoginAsUserRequest,
  ): Promise<LoginResponse> {
    return this.request<LoginResponse>(
      `${this.basePath}/tenant-admin/login-as-user`,
      {
        method: "POST",
        body: JSON.stringify(data),
        disableToast: true,
      },
    );
  }

  async getLoginUrl(data: LoginUrlRequest): Promise<LoginUrlResponse> {
    return this.post<LoginUrlResponse>(`${this.basePath}/login-url`, data);
  }

  async refreshToken(data: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    return this.post<RefreshTokenResponse>(`${this.basePath}/refresh`, data);
  }

  async exchangeCode(data: ExchangeCodeRequest): Promise<LoginResponse> {
    return this.post<LoginResponse>(`${this.basePath}/callback`, data);
  }

  async logout(): Promise<string> {
    const { url } = await this.get<LogoutResponse>(`${this.basePath}/logout`, {
      redirectUri: `${window.location.origin}/login`,
    });
    return url;
  }

  async getMe(options?: { disableToast?: boolean }): Promise<UserProfile> {
    return this.get<UserProfile>(
      `${this.basePath}/me`,
      {},
      {
        ...options,
        skipSelectedHotelHeader: true,
      },
    );
  }
}

export const authService = new AuthService();
